import { css } from '@emotion/react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { formatCentsToCurrency } from '@frontend/currency';
import { ManualTemplateTypesWithStaticTemplate, SendInThreadModalContents } from '@frontend/integrated-messaging';
import { useValidPhoneNumberFromInvoice } from '../../../../hooks/use-valid-phone-number-from-invoice';
import { useValidPhoneNumberFromPerson } from '../../../../hooks/use-valid-phone-number-from-person';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { CollectModalStateCareCreditMessageType } from '../../../collect-payment-multi.types';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CareCreditMessageBodyProps } from './care-credit-message.types';

const styles = {
  loader: css`
    height: 200px;
  `,
  body: css`
    .wv-modal-header {
      display: none;
    }
  `,
};

export const MESSAGE_TYPE_MAP: Record<CollectModalStateCareCreditMessageType, ManualTemplateTypesWithStaticTemplate> = {
  'payment-link': TemplateType_Slug.MANUAL_CARE_CREDIT_COLLECT,
  'pre-approved-link': TemplateType_Slug.MANUAL_CARE_CREDIT_PREAPPROVED,
  'enrollment-link': TemplateType_Slug.MANUAL_CARE_CREDIT_APPLICATION,
};

export const CareCreditMessageBody = ({ onGoBack, onCompleteStep }: CareCreditMessageBodyProps) => {
  const { invoice, activeLocationId, person, careCreditMessageType, careCreditData } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber: firstInvoicePhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  /**
   * Thanks to the CareCredit payments panel, we might have a flow with no invoice.
   * So we needto have a person fallback, too. Internally, the CareCredit multi-steppers only do the valid SMS lookup
   * via person's id, rather than invoice.person.id, etc.
   */
  const { firstValidPhoneNumber: firstPersonPhoneNumber } = useValidPhoneNumberFromPerson(activeLocationId, person);

  const onSend = () => {
    setTimeout(() => onCompleteStep(), 2000);
  };

  const templateType = MESSAGE_TYPE_MAP[careCreditMessageType || 'payment-link'];

  const initPersonPhone = firstInvoicePhoneNumber || firstPersonPhoneNumber;

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.careCreditMessage}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditMessage]}
      hasInternalFooter
      components={{
        Footer: () => null,
      }}
      css={styles.body}
    >
      <SendInThreadModalContents
        groupId={invoice?.locationId || activeLocationId || ''}
        onBack={onGoBack}
        personId={invoice?.person?.id || person?.personId || ''}
        initPersonPhone={initPersonPhone}
        templateType={templateType}
        linkData={{
          link: careCreditData?.patientLink ?? '',
          relatedId: invoice?.id || person?.personId || '',
        }}
        onSchedule={onCompleteStep}
        onSend={onSend}
        onClose={onCompleteStep}
        hideHeader={true}
        contextPreviewProps={
          invoice && {
            iconName: 'pay',
            title: formatCentsToCurrency(invoice?.billedAmount),
            subtitle: formatCentsToCurrency(invoice?.billedAmount),
          }
        }
      />
    </StyledStep>
  );
};
